import { authenticator } from 'otplib';
import QRCode from 'qrcode';
import { toast } from 'react-hot-toast';
import { supabase } from '../supabase';

// Configure authenticator
authenticator.options = { 
  window: 1,  // Time window for token validation
  step: 30    // Time step for token generation (30 seconds)
};

interface TwoFactorSetupResponse {
  secret: string;
  qrCodeUrl: string;
  backupCodes: string[];
}

export class TwoFactorService {
  private static generateBackupCodes(): string[] {
    const codes = [];
    for (let i = 0; i < 10; i++) {
      const code = Math.random().toString(36).substring(2, 8).toUpperCase() +
                  Math.random().toString(36).substring(2, 8).toUpperCase();
      codes.push(code.match(/.{4}/g)?.join('-') || code);
    }
    return codes;
  }

  static async setupTwoFactor(userId: string, userEmail: string): Promise<TwoFactorSetupResponse> {
    try {
      // Generate secret
      const secret = authenticator.generateSecret();
      
      // Generate QR code
      const otpauth = authenticator.keyuri(
        userEmail,
        'Bidzora',
        secret
      );
      
      const qrCodeUrl = await QRCode.toDataURL(otpauth, {
        width: 400,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      });
      
      // Generate backup codes
      const backupCodes = this.generateBackupCodes();

      // Store in Supabase
      const { error } = await supabase
        .from('two_factor')
        .upsert({
          user_id: userId,
          secret,
          backup_codes: backupCodes,
          is_enabled: false,
        });

      if (error) throw error;

      return {
        secret,
        qrCodeUrl,
        backupCodes,
      };
    } catch (error) {
      console.error('Failed to setup 2FA:', error);
      throw new Error('Failed to setup two-factor authentication');
    }
  }

  static async verifyToken(userId: string, token: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('two_factor')
        .select('secret')
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      if (!data?.secret) throw new Error('2FA not set up');

      return authenticator.verify({
        token,
        secret: data.secret
      });
    } catch (error) {
      console.error('Failed to verify 2FA token:', error);
      throw new Error('Failed to verify token');
    }
  }

  static async verifyBackupCode(userId: string, code: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('two_factor')
        .select('backup_codes')
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      if (!data?.backup_codes) return false;

      const isValid = data.backup_codes.includes(code);
      
      if (isValid) {
        // Remove used backup code
        const updatedCodes = data.backup_codes.filter((c: string) => c !== code);
        await supabase
          .from('two_factor')
          .update({ backup_codes: updatedCodes })
          .eq('user_id', userId);
      }

      return isValid;
    } catch (error) {
      console.error('Failed to verify backup code:', error);
      throw new Error('Failed to verify backup code');
    }
  }

  static async enableTwoFactor(userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('two_factor')
        .update({ is_enabled: true })
        .eq('user_id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to enable 2FA:', error);
      throw new Error('Failed to enable two-factor authentication');
    }
  }

  static async disableTwoFactor(userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('two_factor')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to disable 2FA:', error);
      throw new Error('Failed to disable two-factor authentication');
    }
  }

  static async isTwoFactorEnabled(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('two_factor')
        .select('is_enabled')
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      return data?.is_enabled || false;
    } catch (error) {
      console.error('Failed to check 2FA status:', error);
      return false;
    }
  }
}